import React, { useEffect, useCallback, useMemo } from 'react';
import InteractiveField from '../common/InteractiveField';
import DateField from '../common/DateField';
import { convertAmountToWords, getRublesForm } from '../../utils/currency';
import plural from 'plural-ru';
import { getMoneyTransferActNumber } from '../../utils/attachmentNumbers';

/**
 * Компонент "Акт приема-передачи денежных средств"
 * Используется только при наличных платежах
 * Номер приложения вычисляется динамически: всегда 2, если включен
 */
const MoneyTransferActSection = ({ contractData, onChange }) => {
    // Безопасная деструктуризация данных из договора
    const {
        header = {},
        parties = {}
    } = contractData || {};

    // Получаем данные акта из основного договора
    const moneyTransferAct = useMemo(() => contractData?.moneyTransferAct || {}, [contractData?.moneyTransferAct]);
    const { paymentType = 'current', paymentDetails = {} } = moneyTransferAct;

    // Обработчик изменения деталей платежа
    const handlePaymentDetailsChange = useCallback((field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                moneyTransferAct: {
                    ...moneyTransferAct,
                    paymentDetails: {
                        ...paymentDetails,
                        [field]: value
                    }
                }
            });
        }
    }, [onChange, contractData, moneyTransferAct, paymentDetails]);

    // Эффект для автоматического обновления текстового представления суммы
    useEffect(() => {
        if (paymentDetails?.amount) {
            const words = convertAmountToWords(paymentDetails.amount);
            if (words && words !== paymentDetails.amountWords) {
                handlePaymentDetailsChange('amountWords', words);
            }
        } else {
            // Если сумма удалена, очищаем текстовое представление
            if (paymentDetails?.amountWords) {
                handlePaymentDetailsChange('amountWords', '');
            }
        }
    }, [paymentDetails?.amount, paymentDetails?.amountWords, handlePaymentDetailsChange]);

    // Обработчики изменения полей
    const handleHeaderChange = (field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                header: {
                    ...header,
                    [field]: value
                }
            });
        }
    };

    const handlePartiesChange = (field, value) => {
        if (!onChange) return;

        const newParties = { ...parties, [field]: value };
        const signatures = contractData.signatures || { landlord: {}, tenant: {} };
        const updated = {
            ...contractData,
            parties: newParties
        };

        // Двусторонняя синхронизация паспортных данных с разделом "Адреса и реквизиты Сторон"
        if (field === 'landlordPassportSerial' || field === 'landlordPassportNumber') {
            updated.signatures = {
                ...signatures,
                landlord: {
                    ...signatures.landlord,
                    passportSerial: field === 'landlordPassportSerial' ? value : newParties.landlordPassportSerial,
                    passportNumber: field === 'landlordPassportNumber' ? value : newParties.landlordPassportNumber
                }
            };
        }

        if (field === 'tenantPassportSerial' || field === 'tenantPassportNumber') {
            updated.signatures = {
                ...signatures,
                tenant: {
                    ...signatures.tenant,
                    passportSerial: field === 'tenantPassportSerial' ? value : newParties.tenantPassportSerial,
                    passportNumber: field === 'tenantPassportNumber' ? value : newParties.tenantPassportNumber
                }
            };
        }

        onChange(updated);
    };

    const handlePaymentTypeChange = (type) => {
        if (onChange) {
            onChange({
                ...contractData,
                moneyTransferAct: {
                    ...moneyTransferAct,
                    paymentType: type
                }
            });
        }
    };


    // Получаем номер приложения динамически
    const appendixNumber = getMoneyTransferActNumber(contractData);

    return (
        <div className="document-preview-editor money-transfer-act" style={{ marginTop: '60px', pageBreakBefore: 'always' }}>
            {/* Заголовок приложения */}
            <div style={{ textAlign: 'right', marginBottom: '20px', fontSize: '14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ margin: '0 0 5px 0' }}>Приложение № {appendixNumber}</div>
                <div style={{ margin: '0 0 5px 0' }}>
                    к Договору найма жилого помещения № <InteractiveField
                        value={header.number}
                        onChange={(v) => handleHeaderChange('number', v)}
                        placeholder="5716"
                        isRequired={true}
                    />
                </div>
                <div style={{ margin: '0' }}>
                    от <DateField
                        value={header.date}
                        onChange={(v) => handleHeaderChange('date', v)}
                        placeholder="дд.мм.гггг"
                        isRequired={true}
                    /> г.
                </div>
            </div>

            {/* Название акта */}
            <div className="document-title-block">
                <h3>Акт</h3>
                <p>приема-передачи денежных средств.</p>
            </div>

            {/* Дата и место составления акта */}
            <div className="document-location-date">
                <InteractiveField
                    value={header.city}
                    onChange={(v) => handleHeaderChange('city', v)}
                    placeholder="г. Москва"
                    isRequired={true}
                />
                <DateField
                    value={header.date}
                    onChange={(v) => handleHeaderChange('date', v)}
                    placeholder="дд.мм.гггг"
                    isRequired={true}
                />
            </div>

            {/* Преамбула с данными сторон */}
            <p className="document-preamble">
                Гражданин РФ <InteractiveField
                    value={parties.landlordName}
                    onChange={(v) => handlePartiesChange('landlordName', v)}
                    placeholder="Ф.И.О. полностью"
                    isRequired={true}
                />,
                паспорт серии <InteractiveField
                    value={parties.landlordPassportSerial}
                    onChange={(v) => handlePartiesChange('landlordPassportSerial', v)}
                    placeholder="4512"
                    isRequired={true}
                    className="passport-serial-mask"
                />
                {' '}№ <InteractiveField
                    value={parties.landlordPassportNumber}
                    onChange={(v) => handlePartiesChange('landlordPassportNumber', v)}
                    placeholder="654321"
                    isRequired={true}
                    className="passport-number-mask"
                />,
                именуемый(ая) в дальнейшем "Наймодатель", с одной стороны и Гражданин РФ <InteractiveField
                    value={parties.tenantName}
                    onChange={(v) => handlePartiesChange('tenantName', v)}
                    placeholder="Ф.И.О. полностью"
                    isRequired={true}
                />,
                паспорт серии <InteractiveField
                    value={parties.tenantPassportSerial}
                    onChange={(v) => handlePartiesChange('tenantPassportSerial', v)}
                    placeholder="4509"
                    isRequired={true}
                    className="passport-serial-mask"
                />
                {' '}№ <InteractiveField
                    value={parties.tenantPassportNumber}
                    onChange={(v) => handlePartiesChange('tenantPassportNumber', v)}
                    placeholder="123456"
                    isRequired={true}
                    className="passport-number-mask"
                />,
                именуемый(ая) в дальнейшем "Наниматель", с другой стороны, согласно условиям Договора найма жилого помещения № <InteractiveField
                    value={header.number}
                    onChange={(v) => handleHeaderChange('number', v)}
                    placeholder="5716"
                    isRequired={true}
                /> от <DateField
                    value={header.date}
                    onChange={(v) => handleHeaderChange('date', v)}
                    placeholder="дд.мм.гггг"
                    isRequired={true}
                    compact
                /> (далее - Договор) составили настоящий Акт о нижеследующем:
            </p>

            {/* Пункт 1 - Условия оплаты */}
            <div className="dynamic-content-block active-purple">
            <p className="document-preamble">
                1. {paymentType === 'current' && (
                    <>
                        Наниматель передал плату за наем Помещения в виде наличных денежных средств Наймодателю в размере{' '}
                        <InteractiveField
                            value={paymentDetails.amount}
                            onChange={(v) => handlePaymentDetailsChange('amount', v)}
                            placeholder="50000"
                            type="number"
                            integerOnly={true}
                            isRequired={true}
                        />{' '}
                        {paymentDetails.amount && (
                            <span className="amount-words-wrapper">
                                (<span>
                                    {paymentDetails.amountWords || convertAmountToWords(paymentDetails.amount)}
                                </span>)
                            </span>
                        )}{' '}
                        {getRublesForm(paymentDetails.amount)} за текущий месяц, а Наймодатель принял.
                    </>
                )}
                {paymentType === 'previous' && (
                    <>
                        Наниматель передал плату за наем Помещения в виде наличных денежных средств Наймодателю в размере{' '}
                        <InteractiveField
                            value={paymentDetails.amount}
                            onChange={(v) => handlePaymentDetailsChange('amount', v)}
                            placeholder="50000"
                            type="number"
                            integerOnly={true}
                            isRequired={true}
                        />{' '}
                        {paymentDetails.amount && (
                            <span className="amount-words-wrapper">
                                (<span>
                                    {paymentDetails.amountWords || convertAmountToWords(paymentDetails.amount)}
                                </span>)
                            </span>
                        )}{' '}
                        {getRublesForm(paymentDetails.amount)} за прошедший месяц, а Наймодатель принял.
                    </>
                )}
                {paymentType === 'advance' && (
                    <>
                        Наниматель передал предоплату в виде наличных денежных средств Наймодателю в размере{' '}
                        <InteractiveField
                            value={paymentDetails.amount}
                            onChange={(v) => handlePaymentDetailsChange('amount', v)}
                            placeholder="50000"
                            type="number"
                            integerOnly={true}
                            isRequired={true}
                        />{' '}
                        {paymentDetails.amount && (
                            <span className="amount-words-wrapper">
                                (<span>
                                    {paymentDetails.amountWords || convertAmountToWords(paymentDetails.amount)}
                                </span>)
                            </span>
                        )}{' '}
                        {getRublesForm(paymentDetails.amount)} за{' '}
                        <InteractiveField
                            value={paymentDetails.monthsCount}
                            onChange={(v) => handlePaymentDetailsChange('monthsCount', v)}
                            placeholder="1"
                            type="number"
                            isRequired={true}
                        />{' '}
                        {paymentDetails.monthsCount ? plural(paymentDetails.monthsCount, 'месяц', 'месяца', 'месяцев') : 'месяцев'} проживания, а Наймодатель принял.
                    </>
                )}
            </p>

            <div className="option-pills-group" style={{ marginTop: '16px', marginBottom: '16px' }}>
                <button 
                    className={`option-pill ${paymentType === 'current' ? 'selected' : ''}`} 
                    onClick={() => handlePaymentTypeChange('current')}
                >
                    Плата за текущий месяц
                </button>
                <button 
                    className={`option-pill ${paymentType === 'previous' ? 'selected' : ''}`} 
                    onClick={() => handlePaymentTypeChange('previous')}
                >
                    Плата за прошедший месяц
                </button>
                <button 
                    className={`option-pill ${paymentType === 'advance' ? 'selected' : ''}`} 
                    onClick={() => handlePaymentTypeChange('advance')}
                >
                    Предоплата за несколько месяцев
                </button>
            </div>
            </div>

            {/* Пункт 2 */}
            <p className="document-preamble">
                2. Настоящий Акт составлен в двух экземплярах, имеющих равную юридическую силу, по одному для каждой из Сторон.
            </p>

            {/* Пункт 3 */}
            <p className="document-preamble">
                3. Настоящий Акт является неотъемлемой частью Договора.
            </p>
        </div>
    );
};

export default MoneyTransferActSection;